import React, { useEffect, useState } from 'react';
import {
	Box,
	Flex,
	Button,
	Text,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Spinner,
	Badge,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import Locale from '../../../utils/translations';
import { BonfireAPI, ApiType } from '../../../models/BonfireApi';
import config from '../../../utils/config';

interface Session {
	sessionId: string;
	path: string;
	pngCount?: number;
	hasError?: boolean;
}

const IntegrationEvents: React.FC = () => {
	const [sessions, setSessions] = useState<Session[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [filterErrors, setFilterErrors] = useState(false);
	const navigate = useNavigate();

	const fetchSessions = async () => {
		setIsLoading(true);
		const params = filterErrors ? '?filterErrors=true' : '';
		const url = `${config.qa_api.url}/files/screenshots/sessions${params}`;
		const api = new BonfireAPI(ApiType.QaApi);
		try {
			const response = await api.get(url);
			setSessions(response || []);
		} catch (err) {
			console.error('Failed to fetch sessions', err);
			setSessions([]);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchSessions();
	}, [filterErrors]);

	const handleShowDetails = (session: Session) => {
		navigate(`/integration-events/${session.sessionId}`);
	};

	if (isLoading) {
		return (
			<Flex justify="center" align="center" h="50vh">
				<Spinner size="xl" />
			</Flex>
		);
	}

	return (
		<Box>
			<Flex alignItems="center" justifyContent="space-between" mb="23px">
				<Text fontSize="18px" fontWeight="700">
					{Locale.get('Integration Events')}
				</Text>
				<Flex gap="10px">
					<Button
						variant={filterErrors ? 'beMainButton' : 'beSecondaryButton'}
						onClick={() => setFilterErrors(!filterErrors)}
					>
						{filterErrors ? 'Show All' : 'Show Errors Only'}
					</Button>
					<Button variant="beMainButton" onClick={fetchSessions}>
						Refresh
					</Button>
				</Flex>
			</Flex>

			<Table
				variant="unstyled"
				bg="white"
				style={{ borderCollapse: 'separate', borderSpacing: '0 0.5em' }}
			>
				<Thead>
					<Tr>
						<Th>Session ID</Th>
						{filterErrors && (
							<>
								<Th>PNG Count</Th>
								<Th>Status</Th>
							</>
						)}
						<Th>Actions</Th>
					</Tr>
				</Thead>
				<Tbody>
					{sessions.length === 0 ? (
						<Tr>
							<Td colSpan={filterErrors ? 4 : 2} textAlign="center">
								No sessions found
							</Td>
						</Tr>
					) : (
						sessions.map(session => (
							<Tr key={session.sessionId} bg="white" _hover={{ bg: 'gray.50' }}>
								<Td fontWeight="600">{session.sessionId}</Td>
								{filterErrors && (
									<>
										<Td>{session.pngCount || 0}</Td>
										<Td>
											{session.hasError ? (
												<Badge colorScheme="red">Error</Badge>
											) : (
												<Badge colorScheme="green">OK</Badge>
											)}
										</Td>
									</>
								)}
								<Td>
									<Button
										size="sm"
										variant="beSecondaryButton"
										onClick={() => handleShowDetails(session)}
									>
										Show Details
									</Button>
								</Td>
							</Tr>
						))
					)}
				</Tbody>
			</Table>
		</Box>
	);
};

export default IntegrationEvents;
